import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import logo from '../../logo.png';
import { Lock, Plus, Edit2, X, Users, Search, ArrowLeft } from 'lucide-react';

// Roles que requieren estructura (van por endpoint /miembros)
const ROLES_ESTRUCTURA = [3, 4, 5, 6, 7];
// Roles de apoyo (van por endpoint /auth/usuarios)
const ROLES_APOYO = [8, 9, 10, 11]; // 11 = representante_casilla

// Rol → Cargo automático (no puede ser diferente)
const ROL_A_CARGO = {
  2: { id: 1, nombre: 'Coordinador General' },
  3: { id: 2, nombre: 'Coordinador Territorial' },
  4: { id: 3, nombre: 'Coordinador Sectorial' },
  5: { id: 4, nombre: 'Seccional' },
  6: { id: 5, nombre: 'Operador' },
  7: { id: 6, nombre: 'Promotor' },
};

const COLORES_ROL = {
  superadmin:              'bg-red-100 text-red-800',
  coordinador_general:     'bg-purple-100 text-purple-800',
  coordinador_territorial: 'bg-blue-100 text-blue-800',
  coordinador_sectorial:   'bg-indigo-100 text-indigo-800',
  seccional:               'bg-green-100 text-green-800',
  operador:                'bg-yellow-100 text-yellow-800',
  promotor:                'bg-gray-100 text-gray-700',
  auditor:                 'bg-orange-100 text-orange-800',
  responsable_inventario:  'bg-teal-100 text-teal-800',
  movilizador:             'bg-pink-100 text-pink-800',
};

export default function Usuarios() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const desdeAdmin = searchParams.get('desde') === 'admin' || searchParams.get('editar') || searchParams.get('nuevo') === 'true';
  const volver = () => navigate(desdeAdmin ? '/admin' : '/dashboard');

  const puedeGestionar = ['superadmin', 'coordinador_general', 'coordinador_territorial', 'coordinador_sectorial', 'seccional', 'operador'].includes(user?.rol);
  // Solo superadmin y coord_general pueden crear y eliminar usuarios
  const puedeCrear = ['superadmin', 'coordinador_general', 'coordinador_territorial', 'coordinador_sectorial', 'seccional', 'operador'].includes(user?.rol);
  const puedeEliminar = ['superadmin', 'coordinador_general'].includes(user?.rol);

  const [usuarios, setUsuarios] = useState([]);
  const [usuariosFiltrados, setUsuariosFiltrados] = useState([]);
  const [roles, setRoles] = useState([]);
  const [catalogos, setCatalogos] = useState({
    estructuras: [], secciones: [], grupos: [], miembros_para_lider: []
  });
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [filtroRol, setFiltroRol] = useState('');
  const [mostrarModal, setMostrarModal] = useState(false);
  const [mostrarModalEditar, setMostrarModalEditar] = useState(false);
  const [usuarioEditando, setUsuarioEditando] = useState(null);
  const [mensaje, setMensaje] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [procesando, setProcesando] = useState(false);
  const [crearNuevaEstructura, setCrearNuevaEstructura] = useState(false);
  const [casillasDisponibles, setCasillasDisponibles] = useState([]);
  const [casillasEditarDisponibles, setCasillasEditarDisponibles] = useState([]);
  const [seccionEditar, setSeccionEditar] = useState('');
  const [nuevaEstructura, setNuevaEstructura] = useState({ nombre_estructura: '', tipo_estructura: 'Territorial' });


  const formInicial = {
    nombre: '', apellido_paterno: '', apellido_materno: '',
    telefono: '', email: '', rol_id: '',
    estructura_id: '', seccion_id: '', grupo_politico_id: '',
    lider_inmediato_id: '', fecha_inicio: '', observaciones: '',
    ocupacion_general: '', nivel_estudios: '', casilla_id: ''
  };
  const [form, setForm] = useState(formInicial);

  const [formEditar, setFormEditar] = useState({
    nombre: '', apellido_paterno: '', apellido_materno: '',
    telefono: '', email: '', rol_id: '', estatus: 'activo', password: '',
    lider_inmediato_id: '', estructura_id: '', seccion_id: '',
    grupo_politico_id: '', fecha_inicio: '', observaciones: '',
    ocupacion_general: '', nivel_estudios: ''
  });

  useEffect(() => {
    if (puedeGestionar) fetchData();
    else setLoading(false);
  }, [puedeGestionar]);

  // Detectar si viene desde Admin con ?editar=UUID
  useEffect(() => {
    const editarId = searchParams.get('editar');
    const esNuevo = searchParams.get('nuevo') === 'true';

    if (esNuevo) {
      setMostrarModal(true);
    }

    if (editarId && usuarios.length > 0) {
      const u = usuarios.find(u => u.id === editarId);
      if (u) {
        setUsuarioEditando(u);
        setFormEditar({
          nombre: u.nombre || '', apellido_paterno: u.apellido_paterno || '',
          apellido_materno: u.apellido_materno || '', telefono: u.telefono || '',
          email: u.email || '', rol_id: u.rol_id || '',
          estatus: u.estatus || 'activo', password: '',
          lider_inmediato_id: u.lider_inmediato_id || ''
        });
        setMostrarModalEditar(true);
      }
    }
  }, [searchParams, usuarios]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usRes, rolRes, catRes] = await Promise.all([
        api.get('/auth/usuarios'),
        api.get('/auth/roles'),
        api.get('/miembros/catalogos')
      ]);
      setUsuarios((usRes.data.usuarios || []).filter(u => u.estatus === 'activo'));
      setUsuariosFiltrados((usRes.data.usuarios || []).filter(u => u.estatus === 'activo'));
      setRoles(rolRes.data.roles || []);
      setCatalogos(catRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const term = busqueda.toLowerCase();
    setUsuariosFiltrados(
      usuarios.filter(u => {
        const coincideTexto = `${u.nombre} ${u.apellido_paterno} ${u.telefono} ${u.rol}`.toLowerCase().includes(term);
        const coincideRol = !filtroRol || (u.rol_nombre || u.rol) === filtroRol;
        return coincideTexto && coincideRol;
      })
    );
  }, [busqueda, filtroRol, usuarios]);

  const rolId = parseInt(form.rol_id);
  const esRepCasilla = rolId === 11;

  const cargarCasillasPorSeccion = async (seccionNumero) => {
    if (!seccionNumero) { setCasillasDisponibles([]); return; }
    try {
      const res = await api.get(`/casillas/por-seccion/${seccionNumero}`);
      setCasillasDisponibles(res.data.casillas || []);
    } catch (e) { setCasillasDisponibles([]); }
  };

  const cargarCasillasPorSeccionEditar = async (seccionNumero) => {
    if (!seccionNumero) { setCasillasEditarDisponibles([]); return; }
    try {
      const res = await api.get(`/casillas/por-seccion/${seccionNumero}`);
      setCasillasEditarDisponibles(res.data.casillas || []);
    } catch (e) { setCasillasEditarDisponibles([]); }
  };
  const esRolEstructura = ROLES_ESTRUCTURA.includes(rolId);
  const esRolApoyo = ROLES_APOYO.includes(rolId);
  // Cargo asignado automáticamente según el rol
  const cargoAutomatico = ROL_A_CARGO[rolId] || null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    const camposMayusculas = ['nombre', 'apellido_paterno', 'apellido_materno'];
    const valorFinal = camposMayusculas.includes(name) ? value.toUpperCase() : value;
    setForm(prev => ({ ...prev, [name]: valorFinal }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setResultado(null);
    setProcesando(true);

    try {
      if (esRolEstructura) {
        // Validar que estructura y líder sean obligatorios
        if (!crearNuevaEstructura && !form.estructura_id) {
          setResultado({ error: '⚠️ Debes seleccionar una Estructura para este usuario.' });
          setProcesando(false);
          return;
        }
        if (crearNuevaEstructura && !nuevaEstructura.nombre_estructura.trim()) {
          setResultado({ error: '⚠️ Debes escribir el nombre de la nueva Estructura.' });
          setProcesando(false);
          return;
        }
        if (!form.lider_inmediato_id) {
          setResultado({ error: '⚠️ Debes seleccionar un Líder Inmediato para este usuario.' });
          setProcesando(false);
          return;
        }

        let estructuraId = form.estructura_id;

        // Si eligió crear nueva estructura, crearla primero
        if (crearNuevaEstructura && nuevaEstructura.nombre_estructura.trim()) {
          const estRes = await api.post('/estructuras', {
            nombre_estructura: nuevaEstructura.nombre_estructura.trim().toUpperCase(),
            tipo_estructura: nuevaEstructura.tipo_estructura,
            seccion_id: form.seccion_id || null,
            estatus: 'activo'
          });
          estructuraId = estRes.data.estructura?.id || estRes.data.id;
        }

        const res = await api.post('/miembros', {
          ...form,
          estructura_id: estructuraId,
          cargo_id: cargoAutomatico?.id
        });
        setResultado({ ...res.data, tipo: 'estructura' });
      } else {
        // Roles de apoyo — solo datos básicos
        const ultimosCuatro = form.telefono.slice(-4);
        const passwordTemporal = `DOMINIUM${ultimosCuatro}`;
        const resUsuario = await api.post('/auth/usuarios', {
          nombre: form.nombre,
          apellido_paterno: form.apellido_paterno,
          apellido_materno: form.apellido_materno,
          telefono: form.telefono,
          email: form.email,
          password: passwordTemporal,
          rol_id: form.rol_id
        });
        // Si es representante de casilla y eligió casilla, asignarla
        if (esRepCasilla && form.casilla_id && resUsuario.data?.usuario?.id) {
          const uid = resUsuario.data.usuario.id;
          await api.post(`/casillas/${form.casilla_id}/representante`, {
            user_id: uid,
            nombre_completo: `${form.nombre} ${form.apellido_paterno} ${form.apellido_materno || ''}`.trim(),
            telefono: form.telefono
          });
        }
        setResultado({
          tipo: 'apoyo',
          mensaje: `✅ Usuario creado correctamente.`,
          password_temporal: passwordTemporal
        });
      }
      fetchData();
    } catch (error) {
      setResultado({ error: error.response?.data?.error || 'Error al crear usuario.' });
    } finally {
      setProcesando(false);
    }
  };

  const abrirEditar = async (u) => {
    setUsuarioEditando(u);
    setFormEditar({
      nombre: u.nombre || '', apellido_paterno: u.apellido_paterno || '',
      apellido_materno: u.apellido_materno || '', telefono: u.telefono || '',
      email: u.email || '', rol_id: u.rol_id || '',
      estatus: u.estatus || 'activo', password: '',
      lider_inmediato_id: u.lider_inmediato_id || '',
      estructura_id: u.estructura_id || '',
      banco: u.banco || '',
      numero_cuenta: u.numero_cuenta || '',
      clabe_interbancaria: u.clabe_interbancaria || '',
      seccion_id: u.seccion_id || '',
      grupo_politico_id: u.grupo_politico_id || '',
      fecha_inicio: u.fecha_inicio ? u.fecha_inicio.split('T')[0] : '',
      observaciones: u.observaciones || '',
      ocupacion_general: u.ocupacion_general || '',
      nivel_estudios: u.nivel_estudios || '',
      casilla_id: ''
    });
    // Si es representante de casilla, cargar su casilla actual
    const rolNombre = roles.find(r => r.id === parseInt(u.rol_id))?.nombre;
    if (rolNombre === 'representante_casilla' && u.casilla_seccion) {
      setSeccionEditar(String(u.casilla_seccion));
      await cargarCasillasPorSeccionEditar(u.casilla_seccion);
      setFormEditar(prev => ({ ...prev, casilla_id: u.casilla_id ? String(u.casilla_id) : '' }));
    } else {
      setSeccionEditar('');
    }
    setMostrarModalEditar(true);
  };

  const handleGuardarEditar = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/auth/usuarios/${usuarioEditando.id}`, formEditar);
      // Si es representante de casilla y eligió casilla, asignarla
      const rolEditando = roles.find(r => r.id === parseInt(formEditar.rol_id));
      if (rolEditando?.nombre === 'representante_casilla' && formEditar.casilla_id) {
        await api.post(`/casillas/${formEditar.casilla_id}/representante`, {
          user_id: usuarioEditando.id,
          nombre_completo: `${formEditar.nombre} ${formEditar.apellido_paterno} ${formEditar.apellido_materno || ''}`.trim(),
          telefono: formEditar.telefono
        });
      }
      setMensaje({ tipo: 'exito', texto: 'Usuario actualizado correctamente.' });
      setMostrarModalEditar(false);
      fetchData();
    } catch (error) {
      setMensaje({ tipo: 'error', texto: error.response?.data?.error || 'Error al actualizar.' });
    }
  };

  const cerrarModal = () => {
    setMostrarModal(false);
    setResultado(null);
    setForm(formInicial);
    setCrearNuevaEstructura(false);
    setNuevaEstructura({ nombre_estructura: '', tipo_estructura: 'Territorial' });
  };


  const handleEliminarUsuarioCompleto = async (u) => {
    const confirmado = window.confirm(
      `⚠️ ELIMINAR COMPLETAMENTE a ${u.nombre} ${u.apellido_paterno}\n\n` +
      `Rol: ${(u.rol_nombre||u.rol||'').replace(/_/g, ' ')}\n\n` +
      `Esto borrará permanentemente:\n` +
      `• Su cuenta de usuario\n` +
      `• Su registro en estructura\n` +
      `• Sus datos como promovido\n` +
      `• Sus visitas registradas\n` +
      `• Todo rastro en el sistema\n\n` +
      `¿Estás seguro? Esta acción NO se puede deshacer.`
    );
    if (!confirmado) return;
    try {
      await api.delete(`/auth/usuarios/${u.id}`);
      setMensaje({ tipo: 'exito', texto: `✅ ${u.nombre} ${u.apellido_paterno} eliminado completamente.` });
      fetchData();
    } catch (error) {
      setMensaje({ tipo: 'error', texto: error.response?.data?.error || 'Error al eliminar.' });
    }
  };
  if (!puedeGestionar) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="text-white px-6 py-4 flex items-center justify-between shadow-lg"
          style={{ background: 'linear-gradient(135deg, #14532d 0%, #5c1022 100%)' }}>
          <img src={logo} alt="DOMINIUM" className="h-16" />
          <button onClick={volver}
            className="bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg text-sm transition">
            <ArrowLeft size={18} />
          </button>
        </header>
        <div className="flex flex-col items-center justify-center min-h-[70vh]">
          <Lock className="text-gray-300 mb-4" size={56} />
          <p className="text-xl font-semibold text-gray-500">Acceso restringido</p>
          <button onClick={volver}
            className="mt-6 px-6 py-2 rounded-lg text-white text-sm font-medium hover:opacity-90 transition"
            style={{ background: 'linear-gradient(135deg, #16a34a 0%, #9b1c35 100%)' }}>
            <ArrowLeft size={18} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="text-white px-6 py-4 flex items-center justify-between shadow-lg"
        style={{ background: 'linear-gradient(135deg, #14532d 0%, #5c1022 100%)' }}>
        <img src={logo} alt="DOMINIUM" className="h-16" />
        <button onClick={volver}
          className="bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg text-sm transition">
          <ArrowLeft size={18} />
        </button>
      </header>

      <main className="p-4 md:p-6 max-w-7xl mx-auto">

        <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <Users className="text-green-600" size={28} /> Gestión de Usuarios
            </h2>
            <p className="text-gray-500 text-sm">Administración de accesos al sistema DOMINIUM</p>
          </div>
          {puedeCrear && (
            <button onClick={() => { setMostrarModal(true); setResultado(null); setForm(formInicial); }}
              className="text-white font-semibold px-5 py-2 rounded-xl hover:opacity-90 transition flex items-center gap-2"
              style={{ background: 'linear-gradient(135deg, #16a34a 0%, #9b1c35 100%)' }}>
              <Plus size={18} /> Nuevo Usuario
            </button>
          )}
        </div>

        {mensaje && (
          <div className={`mb-4 p-4 rounded-xl ${
            mensaje.tipo === 'exito' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'
          }`}>{mensaje.texto}</div>
        )}

        {/* Contadores */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {[
            { label: 'Total',        count: usuarios.length,                                                                   color: 'border-gray-400' },
            { label: 'Activos',      count: usuarios.filter(u => u.estatus === 'activo').length,                               color: 'border-green-500' },
            { label: 'Coordinadores',count: usuarios.filter(u => (u.rol_nombre||u.rol||'').includes('coordinador')).length,                      color: 'border-blue-500' },
            { label: 'Operativos',   count: usuarios.filter(u => ['seccional','operador','promotor'].includes(u.rol_nombre||u.rol)).length,  color: 'border-yellow-500' },
            { label: 'Apoyo',        count: usuarios.filter(u => ['auditor','responsable_inventario','movilizador','representante_casilla','superadmin'].includes(u.rol_nombre||u.rol)).length, color: 'border-purple-500' },
          ].map((t, i) => (
            <div key={i} className={`bg-white rounded-xl shadow-sm p-3 text-center border-l-4 ${t.color}`}>
              <p className="text-2xl font-bold text-gray-800">{t.count}</p>
              <p className="text-xs text-gray-500 mt-1">{t.label}</p>
            </div>
          ))}
        </div>

        {/* Buscador + Filtro Rol + Excel */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-3 top-3 text-gray-400" />
              <input value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar por nombre, teléfono o rol..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
            <select value={filtroRol} onChange={e => setFiltroRol(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm">
              <option value="">Todos los roles</option>
              <option value="superadmin">Superadmin</option>
              <option value="coordinador_general">Coordinador General</option>
              <option value="coordinador_territorial">Coordinador Territorial</option>
              <option value="coordinador_sectorial">Coordinador Sectorial</option>
              <option value="seccional">Seccional</option>
              <option value="operador">Operador</option>
              <option value="promotor">Promotor</option>
              <option value="auditor">Auditor</option>
              <option value="movilizador">Movilizador</option>
              <option value="representante_casilla">Representante Casilla</option>
              <option value="responsable_inventario">Responsable Inventario</option>
            </select>
            <button onClick={() => {
              const filas = usuariosFiltrados.map(u => [
                `${u.nombre} ${u.apellido_paterno} ${u.apellido_materno||''}`.trim(),
                u.telefono||'', u.email||'',
                (u.rol_nombre||u.rol||'').replace(/_/g,' ').toUpperCase(),
                u.estatus||'',
                u.banco||'', u.numero_cuenta||'', u.clabe_interbancaria||''
              ]);
              const enc = ['Nombre','Teléfono','Email','Rol','Estatus','Banco','No. Cuenta','CLABE'];
              const tsv = [enc, ...filas].map(r => r.join('\t')).join('\n');
              const blob = new Blob([tsv], { type: 'text/tab-separated-values;charset=utf-8;' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a'); a.href = url; a.download = 'usuarios.xls'; a.click();
              URL.revokeObjectURL(url);
            }} className="flex items-center gap-2 px-4 py-2 bg-green-700 text-white rounded-lg text-sm font-medium hover:bg-green-800 transition whitespace-nowrap">
              ⬇️ Descargar Excel
            </button>
          </div>
        </div>

        {/* Tabla */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[700px]">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Nombre</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Teléfono</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Email</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Rol</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Estatus</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Último acceso</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="7" className="py-8 text-center text-gray-400">Cargando...</td></tr>
                ) : usuariosFiltrados.length === 0 ? (
                  <tr><td colSpan="7" className="py-8 text-center text-gray-400">No se encontraron usuarios</td></tr>
                ) : usuariosFiltrados.map((u, i) => (
                  <tr key={i} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium">{u.nombre} {u.apellido_paterno} {u.apellido_materno || ''}</td>
                    <td className="py-3 px-4 text-gray-600">{u.telefono}</td>
                    <td className="py-3 px-4 text-gray-600">{u.email || '—'}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${COLORES_ROL[u.rol_nombre||u.rol] || 'bg-gray-100 text-gray-700'}`}>
                        {(u.rol_nombre||u.rol||'').replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        u.estatus === 'activo' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>{u.estatus}</span>
                    </td>
                    <td className="py-3 px-4 text-gray-500 text-xs">
                      {u.ultimo_acceso ? new Date(u.ultimo_acceso).toLocaleString('es-MX') : '—'}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1">
                        <button onClick={() => abrirEditar(u)}
                          title="Editar usuario"
                          className="p-1.5 hover:bg-blue-100 rounded-lg transition">
                          <Edit2 size={15} className="text-blue-600" />
                        </button>
                        {/* No permitir eliminar al propio superadmin */}
                        {puedeEliminar && (u.rol_nombre||u.rol) !== 'superadmin' && (
                          <button
                            onClick={() => handleEliminarUsuarioCompleto(u)}
                            title="⚠️ Eliminar completamente del sistema"
                            className="p-1.5 hover:bg-red-100 rounded-lg transition text-xs font-bold text-red-600 border border-red-300 px-2">
                            🗑️
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <p className="text-center text-gray-400 text-xs mt-6">
          DOMINIUM © 2026 — Zacatlán, Puebla | GARCOR Design
        </p>
      </main>

      {/* ══════════════════════════════════════════
          MODAL NUEVO USUARIO
      ══════════════════════════════════════════ */}
      {mostrarModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 sticky top-0 z-10"
              style={{ background: 'linear-gradient(135deg, #14532d 0%, #5c1022 100%)', borderRadius: '1rem 1rem 0 0' }}>
              <h3 className="text-white font-bold text-lg">👤 Nuevo Usuario</h3>
              <button onClick={cerrarModal}
                className="text-white hover:bg-white hover:bg-opacity-20 p-1 rounded transition">
                <X size={20} />
              </button>
            </div>

            <div className="p-6">
              {resultado && !resultado.error ? (
                /* Resultado exitoso */
                <div>
                  <div className="bg-green-50 border border-green-200 rounded-xl p-5 mb-4 text-center">
                    <p className="text-4xl mb-2">🎉</p>
                    <p className="text-green-800 font-bold text-lg">{resultado.mensaje}</p>
                    {resultado.enviado_a_talento && (
                      <p className="text-green-600 text-sm mt-1">⭐ Agregado al Banco de Talento automáticamente</p>
                    )}
                    <div className="mt-4 bg-white border border-green-200 rounded-lg p-4 text-left">
                      <p className="text-xs text-gray-500 mb-1">Contraseña temporal:</p>
                      <p className="font-mono font-bold text-green-700 text-xl">{resultado.password_temporal}</p>
                      <p className="text-xs text-gray-400 mt-2">⚠️ Comparte esta contraseña. Se recomienda cambiarla en el primer acceso.</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button onClick={cerrarModal}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition">
                      Cerrar
                    </button>
                    <button onClick={() => { setResultado(null); setForm(formInicial); }}
                      className="flex-1 text-white font-semibold py-2 rounded-lg hover:opacity-90 transition"
                      style={{ background: 'linear-gradient(135deg, #16a34a 0%, #9b1c35 100%)' }}>
                      + Crear otro
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  {resultado?.error && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                      ❌ {resultado.error}
                    </div>
                  )}

                  {/* PASO 1 — Rol */}
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: '#14532d' }}>
                      1. Selecciona el Rol *
                    </label>
                    <select name="rol_id" value={form.rol_id} onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" required>
                      <option value="">Seleccionar...</option>
                      {roles.map(r => (
                        <option key={r.id} value={r.id}>{r.nombre.replace(/_/g, ' ')}</option>
                      ))}
                    </select>

                    {/* Cargo asignado automáticamente */}
                    {cargoAutomatico && (
                      <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2">
                        <span className="text-blue-600 text-sm">🏛</span>
                        <div>
                          <p className="text-xs text-blue-600 font-medium">Cargo asignado automáticamente:</p>
                          <p className="text-sm font-bold text-blue-800">{cargoAutomatico.nombre}</p>
                        </div>
                      </div>
                    )}
                    {esRolApoyo && (
                      <div className="mt-2 p-2 bg-purple-50 border border-purple-200 rounded-lg text-xs text-purple-700">
                        ⚡ Rol de apoyo — solo se crean los datos básicos del usuario.
                      </div>
                    )}
                    {esRepCasilla && (
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Sección Electoral</label>
                          <select onChange={(e) => cargarCasillasPorSeccion(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                            <option value="">Seleccionar sección...</option>
                            {catalogos.secciones?.map(s => (
                              <option key={s.id} value={s.numero_seccion}>{s.numero_seccion} - {s.municipio}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Casilla</label>
                          <select name="casilla_id" value={form.casilla_id || ''} onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                            <option value="">Seleccionar casilla...</option>
                            {casillasDisponibles.map(c => (
                              <option key={c.id} value={c.id}>
                                {c.tipo_casilla} — {c.nombre_lugar} {c.rep_nombre ? `(Rep: ${c.rep_nombre})` : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* PASO 2 — Datos personales */}
                  {form.rol_id && (
                    <div>
                      <p className="text-sm font-semibold mb-3" style={{ color: '#14532d' }}>
                        2. Datos Personales
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                          <input name="nombre" value={form.nombre} onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" required />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Apellido Paterno *</label>
                          <input name="apellido_paterno" value={form.apellido_paterno} onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" required />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Apellido Materno</label>
                          <input name="apellido_materno" value={form.apellido_materno} onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono * (10 dígitos)</label>
                          <input name="telefono" value={form.telefono} onChange={handleChange}
                            inputMode="numeric" maxLength={10}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" required />
                        </div>
                        {esRolApoyo && (
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                            <input name="email" type="email" value={form.email} onChange={handleChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
                          </div>
                        )}
                        {esRolEstructura && (
                          <>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Ocupación</label>
                              <select name="ocupacion_general" value={form.ocupacion_general} onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                                <option value="">Seleccionar...</option>
                                <option value="comerciante">Comerciante</option>
                                <option value="docente">Docente</option>
                                <option value="profesionista">Profesionista</option>
                                <option value="ama_de_casa">Ama de casa</option>
                                <option value="campesino">Campesino</option>
                                <option value="servidor_publico">Servidor público</option>
                                <option value="empleado_privado">Empleado privado</option>
                                <option value="empresario">Empresario</option>
                                <option value="otro">Otro</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Nivel de Estudios</label>
                              <select name="nivel_estudios" value={form.nivel_estudios} onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                                <option value="">Seleccionar...</option>
                                <option value="primaria">Primaria</option>
                                <option value="secundaria">Secundaria</option>
                                <option value="preparatoria">Preparatoria</option>
                                <option value="tecnico">Técnico</option>
                                <option value="licenciatura">Licenciatura</option>
                                <option value="posgrado">Posgrado</option>
                              </select>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {/* PASO 3 — Datos de estructura */}
                  {esRolEstructura && (
                    <div>
                      <p className="text-sm font-semibold mb-3" style={{ color: '#5c1022' }}>
                        3. Datos de Estructura
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Estructura *</label>
                          <div className="flex gap-2 mb-2">
                            <button type="button"
                              onClick={() => { setCrearNuevaEstructura(false); setForm(prev => ({ ...prev, estructura_id: '' })); }}
                              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition ${!crearNuevaEstructura ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-600 border-gray-300'}`}>
                              Asignar existente
                            </button>
                            {[3,4].includes(rolId) && (
                              <button type="button"
                                onClick={() => { setCrearNuevaEstructura(true); setForm(prev => ({ ...prev, estructura_id: 'nueva' })); }}
                                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition ${crearNuevaEstructura ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-600 border-gray-300'}`}>
                                + Crear nueva
                              </button>
                            )}
                          </div>
                          {crearNuevaEstructura ? (
                            <div className="space-y-2 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                              <p className="text-xs text-purple-700 font-medium">Nueva estructura — se creará automáticamente</p>
                              <input
                                value={nuevaEstructura.nombre_estructura}
                                onChange={e => setNuevaEstructura(prev => ({ ...prev, nombre_estructura: e.target.value }))}
                                placeholder="Nombre de la estructura (ej: Magisterio, Médicos, Comerciantes)"
                                className="w-full px-3 py-2 border border-purple-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
                              <select
                                value={nuevaEstructura.tipo_estructura}
                                onChange={e => setNuevaEstructura(prev => ({ ...prev, tipo_estructura: e.target.value }))}
                                className="w-full px-3 py-2 border border-purple-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500">
                                <option value="Territorial">Territorial</option>
                                <option value="Sectorial">Sectorial</option>
                                <option value="Temática">Temática</option>
                                <option value="Gremial">Gremial</option>
                              </select>
                            </div>
                          ) : (
                            <select name="estructura_id" value={form.estructura_id} onChange={handleChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" required>
                              <option value="">Seleccionar estructura...</option>
                              {catalogos.estructuras?.map(e => (
                                <option key={e.id} value={e.id}>{e.nombre_estructura}</option>
                              ))}
                            </select>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Sección Electoral</label>
                          <select name="seccion_id" value={form.seccion_id} onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                            <option value="">Seleccionar...</option>
                            {catalogos.secciones?.map(s => (
                              <option key={s.id} value={s.id}>{s.numero_seccion} - {s.municipio}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Grupo Político</label>
                          <select name="grupo_politico_id" value={form.grupo_politico_id} onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                            <option value="">Seleccionar...</option>
                            {catalogos.grupos?.map(g => (
                              <option key={g.id} value={g.id}>{g.nombre_grupo}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Líder Inmediato *</label>
                          <select name="lider_inmediato_id" value={form.lider_inmediato_id} onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" required>
                            <option value="">Seleccionar líder...</option>
                            {catalogos.miembros_para_lider?.map(m => (
                              <option key={m.id} value={m.id}>
                                {m.nombre} {m.apellido_paterno} — {m.nombre_cargo}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de Inicio</label>
                          <input type="date" name="fecha_inicio" value={form.fecha_inicio} onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Observaciones</label>
                          <textarea name="observaciones" value={form.observaciones} onChange={handleChange}
                            rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Datos Bancarios */}
                  <div className="border-t pt-4">
                    <p className="text-sm font-semibold mb-3" style={{ color: '#5c1022' }}>💳 Datos Bancarios</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Banco</label>
                        <select name="banco" value={form.banco || ''} onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                          <option value="">Sin banco</option>
                          <option value="BBVA">BBVA</option>
                          <option value="Banamex">Banamex (Citibanamex)</option>
                          <option value="Santander">Santander</option>
                          <option value="Banorte">Banorte</option>
                          <option value="HSBC">HSBC</option>
                          <option value="Scotiabank">Scotiabank</option>
                          <option value="Inbursa">Inbursa</option>
                          <option value="Afirme">Afirme</option>
                          <option value="Banbajio">BanBajío</option>
                          <option value="Azteca">Banco Azteca</option>
                          <option value="Bienestar">Banco del Bienestar</option>
                          <option value="Coppel">BanCoppel</option>
                          <option value="Spin">Spin by OXXO</option>
                          <option value="Mercado Pago">Mercado Pago</option>
                          <option value="Nu">Nu (Nubank)</option>
                          <option value="Hey Banco">Hey Banco</option>
                          <option value="Otro">Otro</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">No. Cuenta / Tarjeta</label>
                        <input name="numero_cuenta" value={form.numero_cuenta || ''} onChange={handleChange}
                          placeholder="16 dígitos de tarjeta o no. cuenta"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">CLABE Interbancaria</label>
                        <input name="clabe_interbancaria" value={form.clabe_interbancaria || ''} onChange={handleChange}
                          placeholder="18 dígitos"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
                      </div>
                    </div>
                  </div>

                  {/* Contraseña temporal */}
                  {form.telefono.length === 10 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-xs text-blue-700">
                        🔐 Contraseña temporal: <strong className="font-mono">DOMINIUM{form.telefono.slice(-4)}</strong>
                        — Se recomienda cambiarla en el primer acceso.
                      </p>
                    </div>
                  )}

                  {form.rol_id && (
                    <div className="flex gap-3 pt-2">
                      <button type="button" onClick={cerrarModal}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition">
                        Cancelar
                      </button>
                      <button type="submit" disabled={procesando}
                        className="flex-1 text-white font-semibold py-2 rounded-lg hover:opacity-90 transition disabled:opacity-50"
                        style={{ background: 'linear-gradient(135deg, #16a34a 0%, #9b1c35 100%)' }}>
                        {procesando ? 'Creando...' : '✅ Crear Usuario'}
                      </button>
                    </div>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL EDITAR ── */}
      {mostrarModalEditar && usuarioEditando && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 sticky top-0 z-10"
              style={{ background: 'linear-gradient(135deg, #14532d 0%, #5c1022 100%)', borderRadius: '1rem 1rem 0 0' }}>
              <h3 className="text-white font-bold text-lg">✏️ Editar Usuario — {usuarioEditando.nombre} {usuarioEditando.apellido_paterno}</h3>
              <button onClick={() => setMostrarModalEditar(false)}
                className="text-white hover:bg-white hover:bg-opacity-20 p-1 rounded transition">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleGuardarEditar} className="p-6 space-y-5">

              {/* SECCIÓN 1 — Rol y Estatus */}
              <div>
                <p className="text-sm font-semibold mb-3" style={{ color: '#14532d' }}>1. Rol y Acceso</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Rol *</label>
                    <select value={formEditar.rol_id} onChange={(e) => setFormEditar({...formEditar, rol_id: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" required>
                      <option value="">Seleccionar rol...</option>
                      {roles.map(r => <option key={r.id} value={r.id}>{r.nombre.replace(/_/g,' ')}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Estatus</label>
                    <select value={formEditar.estatus} onChange={(e) => setFormEditar({...formEditar, estatus: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                      <option value="activo">Activo</option>
                      <option value="inactivo">Inactivo</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nueva Contraseña <span className="text-gray-400 text-xs">(dejar vacío para no cambiar)</span>
                    </label>
                    <input type="password" value={formEditar.password}
                      onChange={(e) => setFormEditar({...formEditar, password: e.target.value})}
                      placeholder="••••••••"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
                  </div>
                </div>
              </div>

              {/* SECCIÓN 2 — Datos Personales */}
              <div>
                <p className="text-sm font-semibold mb-3" style={{ color: '#14532d' }}>2. Datos Personales</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                    <input value={formEditar.nombre} onChange={(e) => setFormEditar({...formEditar, nombre: e.target.value.toUpperCase()})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Apellido Paterno *</label>
                    <input value={formEditar.apellido_paterno} onChange={(e) => setFormEditar({...formEditar, apellido_paterno: e.target.value.toUpperCase()})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Apellido Materno</label>
                    <input value={formEditar.apellido_materno} onChange={(e) => setFormEditar({...formEditar, apellido_materno: e.target.value.toUpperCase()})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono *</label>
                    <input value={formEditar.telefono} onChange={(e) => setFormEditar({...formEditar, telefono: e.target.value})}
                      inputMode="numeric" maxLength={10}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input type="email" value={formEditar.email} onChange={(e) => setFormEditar({...formEditar, email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ocupación</label>
                    <select value={formEditar.ocupacion_general || ''} onChange={(e) => setFormEditar({...formEditar, ocupacion_general: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                      <option value="">Seleccionar...</option>
                      {['comerciante','docente','profesionista','ama_de_casa','campesino','servidor_publico','empleado_privado','empresario','otro'].map(o =>
                        <option key={o} value={o}>{o.replace(/_/g,' ')}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nivel de Estudios</label>
                    <select value={formEditar.nivel_estudios || ''} onChange={(e) => setFormEditar({...formEditar, nivel_estudios: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                      <option value="">Seleccionar...</option>
                      {['primaria','secundaria','preparatoria','tecnico','licenciatura','posgrado'].map(n =>
                        <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* SECCIÓN 3 — Datos de Estructura */}
              <div>
                <p className="text-sm font-semibold mb-3" style={{ color: '#5c1022' }}>3. Datos de Estructura</p>
                {parseInt(formEditar.rol_id) !== 11 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Estructura</label>
                    <select value={formEditar.estructura_id || ''} onChange={(e) => setFormEditar({...formEditar, estructura_id: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                      <option value="">Sin estructura</option>
                      {catalogos.estructuras?.map(e => <option key={e.id} value={e.id}>{e.nombre_estructura}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sección Electoral</label>
                    <select value={formEditar.seccion_id || ''} onChange={(e) => setFormEditar({...formEditar, seccion_id: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                      <option value="">Seleccionar...</option>
                      {catalogos.secciones?.map(s => <option key={s.id} value={s.id}>{s.numero_seccion} - {s.municipio}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Grupo Político</label>
                    <select value={formEditar.grupo_politico_id || ''} onChange={(e) => setFormEditar({...formEditar, grupo_politico_id: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                      <option value="">Seleccionar...</option>
                      {catalogos.grupos?.map(g => <option key={g.id} value={g.id}>{g.nombre_grupo}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Líder Inmediato</label>
                    <select value={formEditar.lider_inmediato_id || ''}
                      onChange={(e) => setFormEditar({...formEditar, lider_inmediato_id: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                      <option value="">Sin líder asignado</option>
                      {catalogos.miembros_para_lider?.filter(m => m.id !== usuarioEditando?.id).map(m => (
                        <option key={m.id} value={m.id}>{m.nombre} {m.apellido_paterno} — {m.nombre_cargo || m.rol_nombre}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de Inicio</label>
                    <input type="date" value={formEditar.fecha_inicio || ''}
                      onChange={(e) => setFormEditar({...formEditar, fecha_inicio: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Observaciones</label>
                    <input value={formEditar.observaciones || ''}
                      onChange={(e) => setFormEditar({...formEditar, observaciones: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
                  </div>
                </div>
                )}

                {/* Casilla para representante */}
              {roles.find(r => r.id === parseInt(formEditar.rol_id))?.nombre === 'representante_casilla' && (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sección Electoral</label>
                    <select value={seccionEditar} onChange={(e) => { setSeccionEditar(e.target.value); cargarCasillasPorSeccionEditar(e.target.value); }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="">Seleccionar sección...</option>
                      {catalogos.secciones?.map(s => (
                        <option key={s.id} value={String(s.numero_seccion)}>{s.numero_seccion} - {s.municipio}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Casilla</label>
                    <select value={formEditar.casilla_id || ''} onChange={(e) => setFormEditar({...formEditar, casilla_id: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="">Seleccionar casilla...</option>
                      {casillasEditarDisponibles.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.tipo_casilla} — {c.nombre_lugar} {c.rep_nombre ? `(Rep: ${c.rep_nombre})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Datos Bancarios */}
                <div className="border-t pt-4 mt-2">
                  <p className="text-sm font-semibold mb-3" style={{ color: '#5c1022' }}>💳 Datos Bancarios</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Banco</label>
                      <select value={formEditar.banco || ''} onChange={(e) => setFormEditar({...formEditar, banco: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                        <option value="">Sin banco</option>
                        <option value="BBVA">BBVA</option>
                        <option value="Banamex">Banamex (Citibanamex)</option>
                        <option value="Santander">Santander</option>
                        <option value="Banorte">Banorte</option>
                        <option value="HSBC">HSBC</option>
                        <option value="Scotiabank">Scotiabank</option>
                        <option value="Inbursa">Inbursa</option>
                        <option value="Afirme">Afirme</option>
                        <option value="Banbajio">BanBajío</option>
                        <option value="Azteca">Banco Azteca</option>
                        <option value="Bienestar">Banco del Bienestar</option>
                        <option value="Coppel">BanCoppel</option>
                        <option value="Spin">Spin by OXXO</option>
                        <option value="Mercado Pago">Mercado Pago</option>
                        <option value="Nu">Nu (Nubank)</option>
                        <option value="Hey Banco">Hey Banco</option>
                        <option value="Otro">Otro</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">No. Cuenta / Tarjeta</label>
                      <input value={formEditar.numero_cuenta || ''} onChange={(e) => setFormEditar({...formEditar, numero_cuenta: e.target.value})}
                        placeholder="16 dígitos de tarjeta o no. cuenta"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">CLABE Interbancaria</label>
                      <input value={formEditar.clabe_interbancaria || ''} onChange={(e) => setFormEditar({...formEditar, clabe_interbancaria: e.target.value})}
                        placeholder="18 dígitos"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setMostrarModalEditar(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition">
                  Cancelar
                </button>
                <button type="submit"
                  className="flex-1 text-white font-semibold py-2 rounded-lg hover:opacity-90 transition"
                  style={{ background: 'linear-gradient(135deg, #16a34a 0%, #9b1c35 100%)' }}>
                  Guardar Cambios
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
